import React, { ReactElement, useRef } from 'react'
import cx from 'classnames'
import * as styles from '../../styles/components/content-blocks/table.module.scss'
import reactElementToString from '../../utils/react-element-to-string'
import Heading from '../heading'
import SmallTitle from '../small-title'
import CaptionText from '../caption-text'
import ContentWrapper from '../content-wrapper'
import { useScroll, useTransform } from 'framer-motion'
import { bezierEaseIn } from '../../utils/styles'
import { FillColor } from '../color-fill'

export interface TableCell {
    title?: string
    body?: ReactElement | string
}

type ColorVariant = 'light' | 'dark'

export interface TableBlockProps {
    heading?: string
    rows: string[][] | TableCell[][]
    backgroundType?: FillColor
    textColorVariant?: ColorVariant
}

const TableBlock = ({ rows, heading, textColorVariant = 'dark', backgroundType = 'white' }: TableBlockProps) => {
    const targetRef = useRef<HTMLDivElement>(null)
    const { scrollYProgress } = useScroll({ target: targetRef, offset: ['start end', 'start start'] })
    const opacity = useTransform(scrollYProgress, [0.1, 0.7], [0, 1], { ease: bezierEaseIn })

    return (
        <>
            <ContentWrapper
                ref={targetRef}
                className={cx(styles.container, styles[textColorVariant])}
                style={{ opacity }}
                color={backgroundType}
            >
                <div className={styles.contentContainer}>
                    {heading && <Heading className={styles.heading}>{heading}</Heading> }

                    <table className={styles.table}>
                        <tbody className={styles.tableBody}>
                            {rows.map((row, index) =>
                                <Row cells={row} key={index} rowIndex={index} />,
                            )}
                        </tbody>
                    </table>
                </div>
            </ContentWrapper>
        </>
    )
}

const TableBorder = ({ className }: {className: string}) => {
    return (
        <div className={className} />
    )
}

const Row = ({ cells, rowIndex }: {cells: string[] | TableCell[], rowIndex: number}) => {
    const cellsClean = cells.map((cell) => {
        if (typeof cell === 'string') {
            return { title: cell }
        }
        return cell
    })

    return (
        <tr className={styles.row}>
            {cellsClean.map((cell, index) =>
                <td
                    className={cx(styles.cell, { [styles.empty]: !cell.title && !cell.body })}
                    key={`${index}-${rowIndex}`}
                >
                    {cell.title &&
                        <SmallTitle
                            className={styles.cellTitle}
                            as={'h4'}
                        >
                            {cell.title}
                        </SmallTitle>
                    }
                    {typeof cell.body === 'string' &&
                        <CaptionText
                            dangerouslySetInnerHTML={{ __html: reactElementToString(cell.body) }}
                        />
                    }
                    {cell.body && typeof cell.body !== 'string' &&
                        cell.body
                    }
                    <TableBorder className={styles.borderLeft} />
                    <TableBorder className={styles.borderRight} />
                    <TableBorder className={styles.borderTop} />
                    <TableBorder className={styles.borderBottom} />
                </td>,
            )}
        </tr>
    )
}

export default TableBlock
